/**
 * Reload the page when a dynamic import fails because the chunk no
 * longer exists on the server. This typically happens after a deploy:
 * the user's tab still holds the old manifest and tries to fetch
 * `/_nuxt/<old-hash>.js`, which 404/500s.
 *
 * Nuxt's `experimental.emitRouteChunkError: 'reload'` covers route
 * navigations. This plugin handles the other case — a component
 * already on the page calling `defineAsyncComponent` or `import()`
 * after a redeploy. Without it, the user sees a broken UI until they
 * manually refresh.
 *
 * We use sessionStorage to break reload loops: if we just reloaded
 * and the new build is ALSO broken (e.g. a missing chunk in the fresh
 * manifest), we surface the error instead of looping.
 */
const RELOAD_KEY = 'nuxt-chunk-reload'

export default defineNuxtPlugin((nuxtApp) => {
    const tryReload = (reason: string) => {
        const last = sessionStorage.getItem(RELOAD_KEY)
        const now = Date.now()
        if (last && now - Number(last) < 10_000) {
            console.error('[chunk-error] suppressed reload loop, original reason:', reason)
            return
        }
        sessionStorage.setItem(RELOAD_KEY, String(now))
        console.warn('[chunk-error] reloading page:', reason)
        window.location.reload()
    }

    nuxtApp.hook('app:chunkError', ({ error }) => {
        tryReload(error?.message || 'unknown')
    })

    // Some dynamic-import failures bubble out as unhandled promise
    // rejections rather than via the Nuxt hook (e.g. when triggered
    // from outside Vue's lifecycle). Catch those too.
    window.addEventListener('unhandledrejection', (ev) => {
        const msg = String(ev.reason?.message || ev.reason || '')
        if (/Failed to fetch dynamically imported module|Loading (chunk|CSS chunk).*failed|Importing a module script failed/i.test(msg)) {
            tryReload(msg)
        }
    })
})
